import React, { useEffect, useState } from 'react';
import { hrmsAllReferrals } from '../../lib/hrmsApi';
import { Badge } from '../../components/ui/badge';
import { Users, Trophy, IndianRupee } from 'lucide-react';

const STAGE_COLORS = {
  applied: 'bg-slate-500', screening: 'bg-slate-500', shortlisted: 'bg-blue-500',
  interview_scheduled: 'bg-amber-500', interview_completed: 'bg-amber-600',
  selected: 'bg-emerald-500', offer_sent: 'bg-blue-600', offer_accepted: 'bg-emerald-600',
  joined: 'bg-emerald-700', rejected: 'bg-red-500', offer_rejected: 'bg-red-500',
};

const ReferralsAdminPage = () => {
  const [data, setData] = useState({ referrals: [], leaderboard: [] });
  useEffect(() => { hrmsAllReferrals().then(setData).catch(() => {}); }, []);

  const totalPaid = data.leaderboard.reduce((s, r) => s + (r.reward_paid || 0), 0);
  const totalPending = data.leaderboard.reduce((s, r) => s + (r.reward_pending || 0), 0);

  return (
    <div data-testid="referrals-admin-page">
      <h1 className="text-2xl font-extrabold flex items-center gap-2"><Users className="h-6 w-6 text-orange-500" /> Employee referrals</h1>
      <p className="text-sm text-slate-500 mt-1">Track who's bringing in talent — and who deserves a reward.</p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-white border p-4"><div className="text-[11px] uppercase text-slate-500">Referred applications</div><div className="text-2xl font-extrabold">{data.referrals.length}</div></div>
        <div className="rounded-2xl bg-white border p-4"><div className="text-[11px] uppercase text-slate-500">Referrers</div><div className="text-2xl font-extrabold">{data.leaderboard.length}</div></div>
        <div className="rounded-2xl bg-white border p-4"><div className="text-[11px] uppercase text-slate-500 flex items-center gap-1"><IndianRupee className="h-3 w-3" /> Rewards pending</div><div className="text-2xl font-extrabold text-amber-600">{totalPending.toLocaleString('en-IN')}</div></div>
        <div className="rounded-2xl bg-white border p-4"><div className="text-[11px] uppercase text-slate-500 flex items-center gap-1"><IndianRupee className="h-3 w-3" /> Rewards paid</div><div className="text-2xl font-extrabold text-emerald-600">{totalPaid.toLocaleString('en-IN')}</div></div>
      </div>

      <h2 className="mt-8 text-lg font-bold flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Leaderboard</h2>
      <div className="mt-3 rounded-2xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-4 py-3">#</th>
            <th className="text-left px-4 py-3">Employee</th>
            <th className="text-right px-4 py-3">Referrals</th>
            <th className="text-right px-4 py-3">Interviewed</th>
            <th className="text-right px-4 py-3">Offered</th>
            <th className="text-right px-4 py-3">Joined</th>
            <th className="text-right px-4 py-3">Reward paid</th>
            <th className="text-right px-4 py-3">Reward pending</th>
          </tr></thead>
          <tbody>
            {data.leaderboard.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-10">No referrals yet. Ask employees to share their personal careers link from <b>/hrms/me</b>.</td></tr>}
            {data.leaderboard.map((r, i) => (
              <tr key={r.referrer_employee_id} className="border-t">
                <td className="px-4 py-3 font-bold text-slate-700">{i + 1}</td>
                <td className="px-4 py-3"><div className="font-medium">{r.referrer_name}</div><div className="text-xs text-slate-500">{r.referrer_email} · {r.referrer_employee_id}</div></td>
                <td className="px-4 py-3 text-right font-semibold">{r.total}</td>
                <td className="px-4 py-3 text-right">{r.interviewed}</td>
                <td className="px-4 py-3 text-right">{r.offered}</td>
                <td className="px-4 py-3 text-right font-bold text-emerald-700">{r.joined}</td>
                <td className="px-4 py-3 text-right font-bold text-emerald-600">₹{r.reward_paid.toLocaleString('en-IN')}</td>
                <td className="px-4 py-3 text-right text-amber-600">₹{r.reward_pending.toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-8 text-lg font-bold">All referred applications</h2>
      <div className="mt-3 rounded-2xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-4 py-3">Candidate</th>
            <th className="text-left px-4 py-3">Role</th>
            <th className="text-left px-4 py-3">Referred by</th>
            <th className="text-left px-4 py-3">Stage</th>
          </tr></thead>
          <tbody>
            {data.referrals.map((a) => (
              <tr key={a.id} className="border-t">
                <td className="px-4 py-3 font-medium">{a.full_name}<div className="text-xs text-slate-500">{a.email}</div></td>
                <td className="px-4 py-3">{a.job_title}</td>
                <td className="px-4 py-3">{a.referrer_name}<div className="text-xs text-slate-500">{a.referrer_employee_id}</div></td>
                <td className="px-4 py-3"><Badge className={STAGE_COLORS[a.stage] || 'bg-slate-500'}>{(a.stage || '').replace(/_/g, ' ')}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReferralsAdminPage;
